import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/Text';
import InfoIcon from '@cartwhl/ui/icons/InfoIcon';

import {
  AnimationMainPlayer,
  CameraButtons,
  Footer,
} from 'components/AnimationPlayer';
import { StudioLayout } from 'components/StudioLayout';
import { VideoOverlay } from 'components/StudioLayout';
import LegacyProjectModal from 'components/modals/LegacyProjectModal';
import { animationPlayerStore, navigationStore, userStore } from 'stores';

// Cutoff date: November 16, 2025 (end of day)
const LEGACY_PROJECT_CUTOFF_DATE = new Date('2025-11-16T23:59:59.999Z');

function LegacyProjectInfoButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="pointer-events-none flex h-full w-full flex-col items-center justify-end">
      <div className="pointer-events-auto relative z-0 w-fit max-w-full lg:w-fit">
        <div
          onClick={onClick}
          className="dark:bg-gray-75/45 dark:hover:bg-gray-75/55 cursor-pointer rounded-2xl bg-white/25 backdrop-blur-md transition-all hover:bg-white/35 lg:rounded-[24px]"
          style={{
            boxShadow:
              '2px 2px 4px 0 rgba(0, 0, 0, 0.04), 8px 16px 24px 0 rgba(0, 0, 0, 0.04), 8px 32px 64px 0 rgba(0, 0, 0, 0.10)',
          }}
        >
          <div className="relative z-10 flex min-w-[280px] flex-col items-center gap-2 p-2 lg:min-w-0 lg:px-4 lg:py-3">
            <Text
              variant="studio/body/medium/sm"
              className="text-gray-90 dark:text-gray-15 flex items-center gap-2"
            >
              <span>Legacy project info</span>
              <InfoIcon className="relative bottom-px flex-none" />
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Project() {
  const playerStore = animationPlayerStore.defaultInstance;
  const [showLegacyModal, setShowLegacyModal] = useState(false);
  const navigationState = useSnapshot(navigationStore.state);
  const { userInfo } = useSnapshot(userStore.state);
  const isHomeRoute = navigationStore.matchesRoute('home');

  // Check if user account was created on or before the cutoff date
  const userCreatedAt = userInfo?.createdAt
    ? new Date(userInfo.createdAt)
    : null;
  const shouldShowLegacyProject =
    userCreatedAt && userCreatedAt <= LEGACY_PROJECT_CUTOFF_DATE;

  useEffect(() => {
    // Redirect users who don't qualify away from /home
    if (isHomeRoute && !shouldShowLegacyProject && userInfo?.userID) {
      navigationStore.navigate('/projects');
      return;
    }

    // Show legacy project modal when navigating to /home (legacy project)
    if (isHomeRoute && shouldShowLegacyProject) {
      setShowLegacyModal(true);
    } else {
      setShowLegacyModal(false);
    }
  }, [
    navigationState.path,
    isHomeRoute,
    shouldShowLegacyProject,
    userInfo?.userID,
  ]);

  return (
    <>
      <StudioLayout
        player={<AnimationMainPlayer />}
        cameraButtons={
          <CameraButtons animationPlayerStore={playerStore} variant="full" />
        }
        prompInput={
          <LegacyProjectInfoButton onClick={() => setShowLegacyModal(true)} />
        }
        overlays={<VideoOverlay />}
        footer={<Footer />}
      />
      <LegacyProjectModal
        open={showLegacyModal}
        onClose={() => setShowLegacyModal(false)}
      />
    </>
  );
}
